import { useMemo } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  PiggyBank, 
  CreditCard,
  Receipt,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/foco-calculations';

interface Transaction {
  id: string;
  type: string;
  category: string;
  amount: number;
  date: string;
  notes?: string;
}

interface RecentActivityProps {
  transactions: Transaction[];
  currency: string;
  className?: string;
}

const typeConfig: Record<string, { icon: typeof TrendingUp; color: string; label: string }> = {
  income: { icon: TrendingUp, color: 'text-success bg-success/10', label: 'Ingreso' },
  fixed_expense: { icon: Wallet, color: 'text-destructive bg-destructive/10', label: 'Gasto fijo' },
  variable_expense: { icon: CreditCard, color: 'text-warning bg-warning/10', label: 'Gasto var.' },
  saving: { icon: PiggyBank, color: 'text-primary bg-primary/10', label: 'Ahorro' },
  investment: { icon: ArrowUpRight, color: 'text-success bg-success/10', label: 'Inversión' },
  debt_payment: { icon: ArrowDownRight, color: 'text-muted-foreground bg-muted', label: 'Pago deuda' },
};

const categoryLabels: Record<string, string> = {
  // Ingresos
  salary: 'Salario',
  business: 'Negocio propio',
  freelance: 'Freelance',
  investments_income: 'Rendimientos',
  rental: 'Rentas / Alquileres',
  bonus: 'Bonos / Aguinaldo',
  commissions: 'Comisiones',
  dividends: 'Dividendos',
  pension: 'Pensión',
  gifts_received: 'Regalos recibidos',
  refunds: 'Reembolsos',
  other_income: 'Otros ingresos',
  ingresos_extras: 'Ingresos extras',
  // Gastos fijos
  housing: 'Vivienda',
  rent: 'Alquiler',
  insurance: 'Seguros',
  utilities: 'Servicios',
  utilities_electricity: 'Electricidad',
  utilities_water: 'Agua',
  utilities_gas: 'Gas',
  internet: 'Internet',
  phone: 'Teléfono',
  cable_streaming: 'Streaming',
  insurance_health: 'Seguro salud',
  insurance_life: 'Seguro vida',
  insurance_car: 'Seguro auto',
  insurance_home: 'Seguro hogar',
  subscriptions: 'Suscripciones',
  education_tuition: 'Colegiatura',
  childcare: 'Guardería',
  transport_fixed: 'Transporte fijo',
  condominium: 'Condominio',
  domestic_help: 'Empleada',
  alimony: 'Pensión alimenticia',
  membership: 'Membresías',
  other_fixed: 'Otros fijos',
  alquileres: 'Alquileres',
  // Gastos variables
  food_groceries: 'Supermercado',
  food_restaurants: 'Restaurantes',
  food_delivery: 'Delivery',
  transport_fuel: 'Combustible',
  transport_public: 'Transporte público',
  transport_uber: 'Uber / Taxi',
  transport_parking: 'Parqueo',
  entertainment_movies: 'Cine',
  entertainment_events: 'Eventos',
  entertainment_hobbies: 'Hobbies',
  entertainment_nightlife: 'Salidas',
  health_medicine: 'Medicamentos',
  health_doctor: 'Consultas médicas',
  health_dental: 'Dentista',
  health_gym: 'Gimnasio',
  personal_care: 'Cuidado personal',
  clothing: 'Ropa',
  electronics: 'Electrónicos',
  home_maintenance: 'Mantenimiento hogar',
  car_maintenance: 'Mantenimiento auto',
  pets: 'Mascotas',
  beauty: 'Belleza',
  real_estate: 'Bienes raíces',
  gifts: 'Regalos',
  donations: 'Donaciones',
  education_courses: 'Cursos',
  education_books: 'Libros',
  travel: 'Viajes',
  household_items: 'Artículos hogar',
  other_variable: 'Otros variables',
  food: 'Alimentación',
  transport: 'Transporte',
  entertainment: 'Entretenimiento',
  health: 'Salud',
  education: 'Educación',
  shopping: 'Compras',
  personal: 'Personal',
  mascotas: 'Mascotas',
  belleza: 'Belleza',
  bienes_raices: 'Bienes raíces',
  gastos_hogar: 'Gastos del hogar',
  gastos_personales: 'Gastos personales',
  cursos: 'Cursos',
  mentorias: 'Mentorías',
  // Ahorro
  emergency_fund: 'Fondo emergencia',
  sinking_fund: 'Fondo imprevistos',
  vacation_fund: 'Fondo vacaciones',
  car_fund: 'Fondo auto',
  home_fund: 'Fondo casa',
  education_fund: 'Fondo educativo',
  wedding_fund: 'Fondo boda',
  baby_fund: 'Fondo bebé',
  medical_fund: 'Fondo médico',
  business_fund: 'Fondo negocio',
  christmas_fund: 'Fondo navideño',
  general_savings: 'Ahorro general',
  other_savings: 'Otro ahorro',
  // Inversión
  investment_etf: 'ETFs',
  investment_stocks: 'Acciones',
  investment_bonds: 'Bonos',
  investment_mutual_funds: 'Fondos mutuos',
  investment_crypto: 'Criptomonedas',
  investment_real_estate: 'Bienes raíces',
  investment_reit: 'REITs',
  investment_retirement: 'Retiro',
  investment_business: 'Negocio',
  investment_p2p: 'Préstamos P2P',
  investment_gold: 'Oro',
  investment_forex: 'Forex',
  investment_art: 'Arte',
  investment_other: 'Otras inversiones',
  inversiones_adicionales: 'Inversiones adicionales',
  // Pago de deudas
  credit_card: 'Tarjeta crédito',
  personal_loan: 'Préstamo personal',
  mortgage: 'Hipoteca',
  car_loan: 'Crédito auto',
  student_loan: 'Crédito educativo',
  business_loan: 'Préstamo negocio',
  family_loan: 'Préstamo familiar',
  medical_debt: 'Deuda médica',
  tax_debt: 'Deuda impuestos',
  other_debt: 'Otras deudas',
  loan: 'Préstamo',
  other: 'Otro',
};

export function RecentActivity({ transactions, currency, className }: RecentActivityProps) {
  const recentTransactions = useMemo(() => {
    return transactions
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);
  }, [transactions]);

  if (recentTransactions.length === 0) {
    return (
      <div className={cn('rounded-xl border bg-card p-4 sm:p-6', className)}>
        <h3 className="text-base sm:text-lg font-semibold mb-4">Actividad reciente</h3>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Receipt className="h-10 w-10 text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">
            No hay transacciones este mes
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Registra tu primera transacción para ver la actividad
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl border bg-card p-4 sm:p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base sm:text-lg font-semibold">Actividad reciente</h3>
        <span className="text-xs text-muted-foreground">{recentTransactions.length} transacciones</span>
      </div>
      
      <div className="space-y-3">
        {recentTransactions.map((tx, index) => {
          const config = typeConfig[tx.type] || typeConfig.variable_expense;
          const Icon = config.icon;
          const isIncome = tx.type === 'income';
          
          return (
            <div 
              key={tx.id}
              className={cn(
                'flex items-center gap-3 p-2.5 rounded-lg transition-all duration-200 hover:bg-muted/50 opacity-0 animate-slide-up',
              )}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className={cn('p-2 rounded-lg flex-shrink-0', config.color)}>
                <Icon className="h-4 w-4" />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {categoryLabels[tx.category] || tx.category.replace(/_/g, ' ')}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {tx.notes ? `${tx.notes} · ` : ''}{format(new Date(tx.date), 'd MMM', { locale: es })} · {config.label}
                </p>
              </div>
              
              <p className={cn(
                'text-sm font-semibold tabular-nums flex-shrink-0',
                isIncome ? 'text-success' : 'text-foreground'
              )}>
                {isIncome ? '+' : '-'}{formatCurrency(tx.amount, currency)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
